import { useCallback, useEffect, useRef, useState } from 'react';

import { resolveValue } from '../utils/TypescriptHelpers';

export class VPCEvent<T> {
    private listeners: ((args: T) => void)[] = [];

    public listenTo(listener: (args: T) => void): () => void {
        this.listeners.push(listener);

        return () => {
            const index = this.listeners.indexOf(listener);
            if (index !== -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    public trigger(args: T) {
        this.listeners.forEach(listener => listener(args));
    }
}

/**
 * useEventCallback - Subscribes to a {@link VPCEvent} and invokes a callback when the event is triggered.
 *
 * This hook allows you to respond to event triggers without causing component re-renders.
 * It is ideal for side effects, analytics, or updating external state.
 *
 * @template T - The type of data carried by the event.
 *
 * @param event - The {@link VPCEvent} instance to subscribe to.
 * @param handler - A stable callback function that will be called with the event arguments whenever the event is triggered.
 *
 * @returns An object with:
 * - `emit(args: T)` — A function that triggers the event manually with the given arguments.
 *
 * @example
 * useEventCallback(userLoginEvent, (user) => {
 *   console.log('User logged in:', user);
 * });
 */
export function useEventCallback<T>(event: VPCEvent<T>, handler: (args: T) => void) {
    const handlerRef = useRef(handler);

    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);

    useEffect(() => {
        const unsubscribe = event.listenTo((args: T) => {
            handlerRef.current(args);
        });

        return unsubscribe;
    }, [event]);

    const emit = useCallback((args: T) => {
        event.trigger(args);
    }, [event]);

    return { emit };
}

/**
 * useEventState - Subscribes to a {@link VPCEvent} and tracks the latest event arguments as state.
 *
 * This hook causes the component to re-render every time the event is triggered,
 * making it useful for components that need to reactively consume event data.
 *
 * The initial value can be provided directly or via a lazy initializer function.
 *
 * @template T - The type of data carried by the event.
 *
 * @param event - The {@link VPCEvent} instance to subscribe to.
 * @param initialArgs - The initial value for the event arguments, or a function returning that value.
 *
 * @returns An object with:
 * - `args: T` — The most recent event arguments.
 * - `version: number` — A monotonically increasing version counter (increments with each event).
 * - `emit(args: T)` — A function that triggers the event manually with the given arguments.
 *
 * @example
 * const { args, version, emit } = useEventState(userStatusEvent, () => ({ status: 'offline' }));
 *
 * useEffect(() => {
 *   console.log('User status changed:', args.status);
 * }, [version]);
 */
export function useEventState<T>(event: VPCEvent<T>, initialArgs?: T | (() => T)) {
    const [state, setState] = useState<{ args: T, version: number }>(() => {
        return {
            args: resolveValue(initialArgs),
            version: 0,
        };
    });

    useEventCallback(event, (args: T) => {
        setState(prev => ({
            args,
            version: prev.version + 1,
        }));
    });

    const emit = useCallback((args: T) => {
        event.trigger(args);
    }, [event]);

    return {
        ...state,
        emit,
    };
}